import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { clientsAPI, servicesAPI, invoicesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Plus, Trash2, Loader2, Pencil, Settings } from 'lucide-react';
import ClientSearchInput from './ClientSearchInput';

const InvoiceForm = ({ 
  open, 
  onOpenChange, 
  preselectedClientId = null,
  preselectedClientName = null,
  onSuccess = () => {} 
}) => {
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState({
    clientId: preselectedClientId || '',
    clientName: preselectedClientName || '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    lineItems: [{ service: '', year: new Date().getFullYear().toString(), description: '', amount: 0 }],
    hstRate: 13,
    taxMode: 'exclusive'
  });
  
  // Service management
  const [serviceDialog, setServiceDialog] = useState(false);
  const [newService, setNewService] = useState({ name: '', description: '', defaultAmount: 0 });
  const [editServiceDialog, setEditServiceDialog] = useState(false);
  const [editService, setEditService] = useState(null);
  
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      fetchClients();
      fetchServices();
      fetchNextInvoiceNumber();
      // Reset form when opening
      setInvoice(prev => ({
        clientId: preselectedClientId || '',
        clientName: preselectedClientName || '',
        invoiceNumber: prev.invoiceNumber || '',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        lineItems: [{ service: '', year: new Date().getFullYear().toString(), description: '', amount: 0 }],
        hstRate: 13,
        taxMode: 'exclusive'
      }));
      setHstOverride(null);
    }
  }, [open, preselectedClientId, preselectedClientName]);

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll({});
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await invoicesAPI.getNextNumber();
      setInvoice(prev => ({
        ...prev,
        invoiceNumber: response.data.nextNumber
      }));
    } catch (error) {
      console.error('Error fetching next invoice number:', error);
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    setInvoice(prev => ({
      ...prev,
      clientId,
      clientName: client?.name || ''
    }));
  };

  const addLineItem = () => {
    setInvoice(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { service: '', year: new Date().getFullYear().toString(), description: '', amount: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    setInvoice(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setInvoice(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          // Auto-fill amount when service is selected
          if (field === 'service') {
            const selectedService = services.find(s => s.name === value);
            if (selectedService?.defaultAmount) {
              updatedItem.amount = selectedService.defaultAmount;
            }
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const [hstOverride, setHstOverride] = useState(null);

  const calculateTotals = () => {
    const lineTotal = invoice.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    if (invoice.taxMode === 'inclusive') {
      const rate = invoice.hstRate / 100;
      const subtotal = lineTotal / (1 + rate);
      const hstAmount = hstOverride !== null ? hstOverride : lineTotal - subtotal;
      const grandTotal = lineTotal;
      return { subtotal: grandTotal - hstAmount, hstAmount, grandTotal };
    } else {
      const subtotal = lineTotal;
      const hstAmount = hstOverride !== null ? hstOverride : subtotal * (invoice.hstRate / 100);
      const grandTotal = subtotal + hstAmount;
      return { subtotal, hstAmount, grandTotal };
    }
  };

  // Service management handlers
  const handleCreateService = async () => {
    if (!newService.name) {
      toast({ title: 'Error', description: 'Service name is required', variant: 'destructive' });
      return;
    }
    try {
      await servicesAPI.create(newService);
      toast({ title: 'Success', description: 'Service created' });
      setNewService({ name: '', description: '', defaultAmount: 0 });
      fetchServices();
    } catch (error) {
      console.error('Error creating service:', error);
      toast({ title: 'Error', description: 'Failed to create service', variant: 'destructive' });
    }
  };

  const openEditServiceDialog = (service) => {
    setEditService({ ...service });
    setEditServiceDialog(true);
  };

  const handleUpdateService = async () => {
    if (!editService?.name) {
      toast({ title: 'Error', description: 'Service name is required', variant: 'destructive' });
      return;
    }
    try {
      await servicesAPI.update(editService._id, {
        name: editService.name,
        description: editService.description,
        defaultAmount: editService.defaultAmount
      });
      toast({ title: 'Success', description: 'Service updated' });
      setEditServiceDialog(false);
      setEditService(null);
      fetchServices();
    } catch (error) {
      console.error('Error updating service:', error);
      toast({ title: 'Error', description: 'Failed to update service', variant: 'destructive' });
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await servicesAPI.delete(serviceId);
      toast({ title: 'Success', description: 'Service deleted' });
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({ title: 'Error', description: 'Failed to delete service', variant: 'destructive' });
    }
  };

  const handleCreateInvoice = async () => {
    if (!invoice.clientId) {
      toast({ title: 'Error', description: 'Please select a client', variant: 'destructive' });
      return;
    }
    if (!invoice.invoiceNumber) {
      toast({ title: 'Error', description: 'Please enter an invoice number', variant: 'destructive' });
      return;
    }
    if (!invoice.dueDate) {
      toast({ title: 'Error', description: 'Please set a due date', variant: 'destructive' });
      return;
    }

    const validLineItems = invoice.lineItems.filter(item => item.service && item.amount > 0);
    if (validLineItems.length === 0) {
      toast({ title: 'Error', description: 'Please add at least one line item with a service and amount', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const { subtotal, hstAmount, grandTotal } = calculateTotals();
      const invoiceData = {
        clientId: invoice.clientId,
        clientName: invoice.clientName,
        invoiceNumber: invoice.invoiceNumber,
        invoiceDate: new Date(invoice.invoiceDate).toISOString(),
        dueDate: new Date(invoice.dueDate).toISOString(),
        lineItems: validLineItems,
        subtotal,
        hstRate: invoice.hstRate / 100,
        hstAmount,
        grandTotal,
        taxMode: invoice.taxMode
      };

      await invoicesAPI.create(invoiceData);
      toast({ title: 'Success', description: 'Invoice created successfully' });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({ title: 'Error', description: 'Failed to create invoice', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Invoice</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {/* Client and Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              {preselectedClientId ? (
                <Input value={preselectedClientName || ''} disabled />
              ) : (
                <ClientSearchInput
                  value={invoice.clientId}
                  onSelect={(clientId, clientName) => {
                    setInvoice(prev => ({ ...prev, clientId, clientName }));
                  }}
                  placeholder="Search clients..."
                  data-testid="invoice-client-select"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Invoice # *</Label>
              <Input
                placeholder="INV-1001"
                value={invoice.invoiceNumber}
                onChange={(e) => setInvoice(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                data-testid="invoice-number"
              />
            </div>
          </div>

          {/* Dates Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Invoice Date</Label>
              <Input
                type="date"
                value={invoice.invoiceDate}
                onChange={(e) => setInvoice(prev => ({ ...prev, invoiceDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={invoice.dueDate}
                onChange={(e) => setInvoice(prev => ({ ...prev, dueDate: e.target.value }))}
                data-testid="invoice-due-date"
              />
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Line Items</Label>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setServiceDialog(true)} 
                  className="gap-1"
                  data-testid="manage-services-btn"
                >
                  <Settings className="h-3 w-3" /> Manage Services
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="gap-1">
                  <Plus className="h-3 w-3" /> Add Line
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {invoice.lineItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    {index === 0 && <Label className="text-xs text-gray-500">Service</Label>}
                    <Select
                      value={item.service}
                      onValueChange={(v) => updateLineItem(index, 'service', v)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service._id} value={service.name}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    {index === 0 && <Label className="text-xs text-gray-500">Year</Label>}
                    <Input
                      placeholder="Year"
                      value={item.year}
                      onChange={(e) => updateLineItem(index, 'year', e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-4">
                    {index === 0 && <Label className="text-xs text-gray-500">Description</Label>}
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-2">
                    {index === 0 && <Label className="text-xs text-gray-500">Amount</Label>}
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={item.amount || ''}
                      onChange={(e) => updateLineItem(index, 'amount', parseFloat(e.target.value) || 0)}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-1">
                    {invoice.lineItems.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        className="h-9 w-9 p-0 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t pt-4">
            <div className="flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm items-center mb-3">
                  <span className="font-medium">Tax Calculation:</span>
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                    <button
                      type="button"
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${invoice.taxMode === 'exclusive' ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                      onClick={() => { setInvoice(prev => ({ ...prev, taxMode: 'exclusive' })); setHstOverride(null); }}
                      data-testid="tax-mode-exclusive"
                    >
                      Exclusive
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${invoice.taxMode === 'inclusive' ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                      onClick={() => { setInvoice(prev => ({ ...prev, taxMode: 'inclusive' })); setHstOverride(null); }}
                      data-testid="tax-mode-inclusive"
                    >
                      Inclusive
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-2">
                  {invoice.taxMode === 'inclusive' 
                    ? 'Amounts include HST — tax is back-calculated from totals' 
                    : 'HST is added on top of the line item amounts'}
                </p>
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>${totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span>GST/HST Rate:</span>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      className="w-16 h-7 text-right"
                      value={invoice.hstRate}
                      onChange={(e) => { setInvoice(prev => ({ ...prev, hstRate: parseFloat(e.target.value) || 0 })); setHstOverride(null); }}
                    />
                    <span>%</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span>GST/HST ({invoice.hstRate}%):</span>
                  <div className="flex items-center gap-1">
                    <span>$</span>
                    <Input
                      type="number"
                      className="w-24 h-7 text-right"
                      value={hstOverride !== null ? hstOverride : totals.hstAmount.toFixed(2)}
                      onChange={(e) => setHstOverride(parseFloat(e.target.value) || 0)}
                      data-testid="hst-amount-input"
                    />
                  </div>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Grand Total:</span>
                  <span>${totals.grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateInvoice} disabled={loading} data-testid="create-invoice-submit">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Create Invoice
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Manage Services Dialog */}
      <Dialog open={serviceDialog} onOpenChange={setServiceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Services</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Existing Services List */}
            {services.length > 0 && (
              <div className="space-y-2">
                <Label className="text-base font-semibold">Existing Services</Label>
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {services.map((service) => (
                    <div key={service._id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{service.name}</p>
                        <p className="text-sm text-gray-500">{service.description || 'No description'}</p>
                        {service.defaultAmount > 0 && (
                          <p className="text-sm text-gray-500">Default: ${service.defaultAmount}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditServiceDialog(service)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteService(service._id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Create New Service Form */}
            <div className="border-t pt-4 space-y-4">
              <Label className="text-base font-semibold">Create New Service</Label>
              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input
                  placeholder="e.g., Tax Return Preparation"
                  value={newService.name}
                  onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Service description"
                  value={newService.description}
                  onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Default Amount</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={newService.defaultAmount || ''}
                  onChange={(e) => setNewService(prev => ({ ...prev, defaultAmount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleCreateService} className="flex-1">
                  Create Service
                </Button>
                <Button variant="outline" onClick={() => setServiceDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={editServiceDialog} onOpenChange={setEditServiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Service</DialogTitle>
          </DialogHeader>
          {editService && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input
                  value={editService.name}
                  onChange={(e) => setEditService(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editService.description || ''}
                  onChange={(e) => setEditService(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Default Amount</Label>
                <Input
                  type="number"
                  value={editService.defaultAmount || ''}
                  onChange={(e) => setEditService(prev => ({ ...prev, defaultAmount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditServiceDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateService}>Update Service</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default InvoiceForm;
